import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0224 from "../lesson-24/lesson";
import lesson0225 from "../lesson-25/lesson";

const linkLesson0224 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0224.id);
const linkLesson0225 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0225.id);

const theory = ` <h3 class=likesectionHead>Research process</h3>
<p>
   Research mostly starts with an interesting observation. With the goal of getting an explanation for the observation, it is a common path to express the issue as a research question. Starting from this point, a full scientific research process will include many steps involving various decisions and different paths. A simplified structure of the research process is shown in <a href="#" data-img-src=${config.ASSETS_URL}/02/23/theory/figure-1.svg data-img-alt="Figure 1. Simplified structure of a research process.">Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/23/theory/figure-1.svg alt="Figure 1. Simplified structure of a research process."><figcaption class=caption>Figure 1. Simplified structure of a research process.</figcaption></figure>
<p>Not all listening experiments have to be conducted using the procedure of a research experiment. However, following a clear procedure will help to reduce biases in your experiment and will make your experiment reproducible, meaning that if the experiment is conducted and evaluated by someone else, the same conclusions are reached (assuming the test participants are of the same population, i.e., the specific group of persons that data is collected from).
<h3 class=likesectionHead>Experimental design</h3>
<h4 class=likesubsectionHead>Hypothesis</h4>
<p>
   Interesting theories based on the observations can be also defined as our research question. For example, while observing how well you are able to estimate a distance of a sound source, you could formulate the theory that this ability depends on the room acoustical environment. Such a prediction is also known as the hypothesis. By setting up the hypothesis, we are expecting an effect (size of difference) to be present: a difference in distance estimation exists for different environments. This alternative hypothesis (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
          <mrow>
            <mo>H</mo>
          </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   ) is what you want to accept using a listening experiment.
<ul class=itemize1>
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
         <mrow>
            <mo>H</mo>
          </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      : Alternative hypothesis: There is a difference between the conditions
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
         <mrow>
            <mo>H</mo>
          </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>
      : Null hypothesis: There is no difference between the conditions
</ul>
<p>
   The null hypothesis
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
            <mo>H</mo>
          </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the opposite of the alternative hypothesis and always states the effect to be absent. In our example, it states that there is no difference in distance perception for different environments. The experiment now needs to be designed in order to test if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
            <mo>H</mo>
          </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   can be rejected and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
            <mo>H</mo>
          </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   can be accepted. However, if you are not able to reject
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
            <mo>H</mo>
          </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   , it does not mean that you can accept
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
      <mrow>
            <mo>H</mo>
          </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   . You can only state that ”<em><span class=cmti-10>there was not enough evidence to reject the null </span><span class=cmti-10>hypothesis</span></em>”.
<h4 class=likesubsectionHead>Variables of the experiment</h4>
<p>
   To check the hypothesis, two types of variables are required: variables which are varied or controlled (independent variables) and variables which can be used to measure the effect (dependent variables). In our example, the independent variable is the (acoustical) environment and the dependent variable is the estimated distance of the sound source. Another term for independent variable would be factor. Different characteristics of a factor are called levels. These can be roughly described by words (e.g., small room or large room) or better, quantified by an appropriate measure (e.g., the room volume). In our example, the acoustic characteristic of the environment can additionally be described by another factor, e.g., the value of the reverberation time. The combination of factors and levels is called condition. In a so-called 2x2 design, we could design an experiment with two factors, the room volume (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   =
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>200</mn>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
      <mrow>
            <mo>m</mo>
          </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   =
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>2000</mn>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
      <mrow>
            <mo>m</mo>
          </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   ,) and the room’s reverberation time (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   =
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   s,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   =
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   s). In the principle part, a 1x3 design is chosen with three different reverberation times (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msub>
   </math>
   ).
<p>In general, variables of experiments are measured or defined in different scales.
<ul class=itemize1>
   <li class=itemize>
      <strong><span class=cmbx-10>Categorical scales</span></strong> contain categories selected experimenter. Such scales can be further divided into two groups
      <ul>
         <li><b>Binary scale</b>  where only between two options are possible, e.g. <em><span class=cmti-10>accurate </span><span class=cmti-10>hit</span></em> or <em><span class=cmti-10>no accurate hit</span></em>.
         <li><b>Nominal scale</b>, which provides more than two possibilities to answer, e.g., <em><span class=cmti-10>male</span></em>, <em><span class=cmti-10>female</span></em>, and <em><span class=cmti-10>diverse</span></em>.
      </ul>
   <li class=itemize><strong><span class=cmbx-10>Ordinal scale</span></strong>, which is used when order matters but distance between values is not relevant, e.g., not accurate, slightly accurate, fairly accurate, quite accurate, very accurate.
   <li class=itemize>
      <strong><span class=cmbx-10>Continuous scale</span></strong>, which can give us a score in the measure which can be any value on the measurement scale. To be more precise the following two types can be used:
      <ul class=itemize2>
         <li class=itemize><strong><span class=cmbx-10>Interval scale</span></strong>, which provides equal intervals between two characteristics ranked next to each other throughout the scale. For example, we can add numbers to represent the previous ordinal scale: not accurate (1), slightly accurate (2), fairly accurate (3), quite accurate (4), very accurate (5).
         <li class=itemize><strong><span class=cmbx-10>Ratio scale</span></strong>, which goes a further step in comparison to the interval scale. The ratios of values along the scale should be meaningful. This means that the scale must have a meaningful zero point. In our example the scale would shift like this: not accurate (-2), slightly accurate (-1), fairly accurate (0), quite accurate (+1), very accurate (+2).
      </ul>
</ul>
<h4 class=likesubsectionHead>Experimental methods</h4>
<p>There are two ways to test a hypothesis when it comes to data collection. The first option is correlational or cross-sectional studies where the researchers do not directly interfere with the situation and only observe what is naturally going on. The second option and the focus of this lesson is experimental research where variables are manipulated in a controlled way and the effects of this manipulation are measured. Both types of hypothesis testing come with different advantages and disadvantages, like artificial test conditions (experimental research) or the need to collect very big amount of data (cross-sectional research). Therefore, the hypothesis test must be chosen carefully depending on the hypothesis.
<h4 class=likesubsectionHead>Data collection</h4>
<p>Within experimental research, there also various ways of collecting data, i.e., it is relevant
<ul class=itemize1>
   <li class=itemize>How participants are asked (Unambiguous expressions are to be used) ?
   <li class=itemize>How questions should be answered by the participants (which scale and which labels is chosen) ?
   <li class=itemize>Which participants are asked and are there groups for different experimental conditions?
</ul>
<p>Related to the first two questions, in step 5 of the principle part, examples are provided of how to differently ask participants for their perception using different expressions for the task and using different scales. More approaches and implementations of experimental methods for acoustical experiments are the topic of <a href="${linkLesson0224}" target="_blank">Lesson 2.24</a>.<br class=newline>The third aspect concerns the way participants are selected and grouped. The method, in which different groups of participants take part in each condition, is called between-subject or between-groups design. In the other method, all participants take part in all conditions (within-subject design). When each condition in a listening experiment is not just presented once, but multiple times, the design is denoted as repeated measures. In this way, more results per condition are obtained and additionally, it can be checked if participants reliably answer the questions. The downside of this approach is that experiments can become very long, potentially leading to fatigue or boredom effects, even if breaks are included. Learning effects should also be avoided, e.g., by counterbalancing, which orders the conditions in a systematic way. By only presenting one condition to the participants, the length of the experiment is lower in a between-subject design - here, however, a larger total number of participants is required. Depending on the statistical evaluation, a power analysis can be conducted to determine the required number of participants for each group and the full experiment can be determined before hand, e.g., using the free power analysis calculator <em><span class=cmti-10>G*Power</span></em> <span class=cite>[<span class=cmbx-10>?</span>]</span>. More details on the statistical evaluation of a listening experiments are discussed in <a href="${linkLesson0225}" target="_blank">Lesson 2.25</a>.
<h4 class=likesubsectionHead>Experimental set-up</h4>
<p>Once the experiment is designed, i.e., one or more hypothesis were formulated, adequate items, scales and analysis methods were selected, the environment of the experiment needs to be prepared. For acoustical experiments, the environment should be shielded from external influences, especially acoustically, but also visually. Often sound-insulated hearing booths are applied, but depending on the type of listening tests and potential noise source in and around the buildings, normal laboratory rooms can also be sufficient, even with multiple test participants being present at the same time, e.g., in jury test to evaluate the sound quality of products.
<p>Besides to the room, the method of audio reproduction is also important for the listening tests. The audio stimuli could be reproduced by one or more loudspeakers, or via headphones. In any case, the choice of the adequate output level for the reproduction device is an essential part of setting up the listening experiments. In some cases, the output levels need to be properly calibrated. Applied technology needs to be robust and reliable in order to provide the stimuli consistently among all participants of the same participant group. This often requires a thorough documentation of the experimental set-up, which is also required when presenting and publishing the results of the listening experiments. Such information enables other researchers to understand the setting of the experiment and makes it possible to confirm the conclusions of the experiments by repeating the experiment. Standardized test methods (e.g., those described in the ITU-R recommendations for the perceptual evaluation of sound systems) also contain specific guidelines regarding the information to include in a listening test report.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>ITU
  <dd>International Telecommunication Union
</dl>
<h3 class=likesectionHead>References</h3>
<dl>
  <dt>[1]
  <dd>S. Bech and N. Zacharov. Perceptual Audio Evaluation–Theory, Method and Application. John Wiley & Sons, Ltd, 2006.
  <dt>[2]
  <dd>F. Faul, E. Erdfelder, A. Buchner, and A.-G. Lang. Statistical power analyses using g*power 3.1: Tests for correlation and regression analyses. Behavior Research Methods, 41(4):1149–1160, 2009.
  <dt>[3]
  <dd>TU-R BS.1116-1. Methods for the subjective assement of small impairments in audio systems including multichannel sound systems, 1997.
  <dt>[4]
  <dd>TU-R BS.1116-3. Methods for the subjective assement of small impairments in audio systems. ITU, Geneva, Switzerland, 2015.
</dl>`;

export default theory;
